import { ResizingMode } from '@betterpool/api-types/contests-service';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { Group, createStyles } from '@mantine/core';

import { BadgeResizable } from '~/components/shared/Badges/Badges';
import { CurrencyConvertor } from '~/components/utils/formatters';
import type { MyUserContest } from '~/domains/contest/domains/myContests/types/userContest';
import { type Contest } from '~/domains/contest/domains/services/interfaces/contest.interface';
import getContestMaxEntriesLabel from '~/domains/contest/domains/gamecenter/domains/common/utils/getContestMaxEntriesLabel';

import type { ContestCardActionBanner } from '../types';
import { useAdditionalPayoutSettings } from '../../../hooks/useAdditionalPayoutSettings';
import PrizeBadge from '../../PrizeBadge/PrizeBadge';

import ContestCardFooterMeta from './ContestCardFooterMeta';

type ContestMetaGenericProps = {
  contest: Contest | MyUserContest;
  actionBanner: ContestCardActionBanner;
  isEntertainmentOnly?: boolean;
};

const useStyles = createStyles((theme) => ({
  prizes: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.other.spacing._2,
    justifyContent: 'end',
  },
}));

function ContestMetaGeneric({
  actionBanner,
  contest,
  isEntertainmentOnly,
}: ContestMetaGenericProps) {
  const { t } = useTranslation('contest');
  const { classes, theme } = useStyles();
  const {
    type,
    isEnabled: isGuaranteedPlusEnabled,
    hasPrizeIncreased,
  } = useAdditionalPayoutSettings(contest);
  const maxEntries = getContestMaxEntriesLabel(contest.entries);
  const meta = [
    {
      value: `${contest.entries.filled}/${maxEntries}`,
      label: (
        <Group spacing={theme.other.spacing._2}>
          {t('common.contestCard.footer.entries')}
          {contest.entries.user ? (
            <span>
              ({contest.entries.user}/
              {contest.entries.max_per_user === 0 ? '∞' : contest.entries.max_per_user})
            </span>
          ) : null}
          {contest.resizing_mode === ResizingMode.RESIZABLE ? (
            <BadgeResizable iconSize={12} />
          ) : null}
        </Group>
      ),
      textAlign: 'flex-start',
    },
    !isEntertainmentOnly && {
      value: CurrencyConvertor(contest.entry_fee / 100),
      label: t('common.contestCard.footer.entry'),
      textAlign: 'center',
    },
    !isEntertainmentOnly && {
      value: CurrencyConvertor(contest.prize_pool / 100),
      label: (
        <div className={classes.prizes}>
          {t('common.contestCard.footer.prizes')}
          <PrizeBadge
            resizingMode={contest.resizing_mode}
            isGuaranteedPlusEnabled={isGuaranteedPlusEnabled}
            additionalPayoutType={type}
            iconSize={12}
            style={{ marginLeft: /* eslint-disable-line local-rules/use-theme-tokens */ 1 }}
          />
        </div>
      ),
      textAlign: 'flex-end',
      valueColor: hasPrizeIncreased ? theme.colors.gold[0] : undefined,
    },
  ].filter(Boolean);

  return <ContestCardFooterMeta actionBanner={actionBanner} meta={meta} />;
}

export default ContestMetaGeneric;
